<script setup>
import { ref, computed, onBeforeMount, onBeforeUnmount, watch } from 'vue'
import { useTranslaterStore } from '@/stores/translater'
import { useAreasStore } from '@/stores/areas'
import { useCarAreasStore } from '@/stores/car-areas'
import { useRoute } from 'vue-router'

// Emits
const emit = defineEmits(['submit-success', 'cancel'])

// Route
const route = useRoute()

// Store
const translaterStore = useTranslaterStore()
const areasStore = useAreasStore()
const carAreasStore = useCarAreasStore()

// Data
const loader = ref(false)
const areas = ref({})
const carAreas = ref([])

const pageNumber = ref(1)
const showAttrs = ref([])
const inputSearch = ref('')

// Computed
const getTranslate = computed(() => translaterStore.getTranslate)
const areasProcess = computed(() => areasStore.areasProcess)
const areasMsg = computed(() => areasStore.areasMsg)
const carAreasCreateMsg = computed(() => carAreasStore.carAreasCreateMsg)
const carAreasDeleteMsg = computed(() => carAreasStore.carAreasDeleteMsg)

function clearCarAreasMsgs() {
    // Очистить все сообщения
    if(Object.keys(carAreasCreateMsg.value).length) carAreasStore.setCarAreasCreateMsg({})
    if(Object.keys(carAreasDeleteMsg.value).length) carAreasStore.setCarAreasDeleteMsg({})
}

function changeCarEvent(event, area) {
    let isChange = true

    // значения в чекбоксах
    const inputs = event.target.form.querySelectorAll('input[type="checkbox"]')
    
    // значения с БД
    const carAreasEvents = []
    
    // текущие значения чекбоксов
    const carAreasEventsChecked = []
    
    // получить значения с БД
    carAreas.value?.forEach(carArea => {
        if(carArea.area == area) {
            carAreasEvents.push(carArea.type)
        }
    })

    // получить текущие значения чекбоксов
    inputs.forEach(elem => {
        if(elem.checked) {
            carAreasEventsChecked.push(elem.value)
        }
    })

    if(carAreasEvents.length !== carAreasEventsChecked.length) {
        isChange = true
    } else {
        isChange = false

        if(carAreasEvents.length < inputs.length && carAreasEventsChecked.length < inputs.length) {
            // проверить совпадают ли
            if(JSON.stringify(carAreasEvents.sort()) !== JSON.stringify(carAreasEventsChecked.sort())) isChange = true
        }
    }

    if(isChange) {
        event.target.form.querySelector('button[type="submit"]').disabled = false
    } else {
        event.target.form.querySelector('button[type="submit"]').disabled = true
    }
}

async function loadCarAreas() {
    const areasArray = areas.value?.array?.map(area => area.uuid)

    const isUseStore = false
    const params = {
        'uuid_car_array': route.params.uuid,
        'uuid_area_array': areasArray,
        'c.limit': 1000
    }

    loader.value = true

    // Получаем связки авто и его зон
    const request = await carAreasStore.readCarAreas(params, isUseStore)
    
    carAreas.value = request.array || []

    loader.value = false
}

function checkSelected(type, uuid) {
    let result = false

    carAreas.value?.forEach(carArea => {
        if(carArea.area == uuid && carArea.type == type) {
            result = true
        }
    })

    return result
}

async function changePage(cursor, dir) {
    clearCarAreasMsgs()

    const isUseStore = true
    const params = {
        'c.limit': 6, 
        'c.direction': dir, 
        'c.cursor': cursor
    }

    const requestAreas = await areasStore.readAreas(params, isUseStore)
    
    if(requestAreas.array) {
        dir == 'after' ? 
            pageNumber.value++ : 
            pageNumber.value--
    }

    areas.value = requestAreas

    await loadCarAreas()
}

async function submitForm(event, area) {
    clearCarAreasMsgs()

    const requestErrors = []

    const payload = {
        car: route.params.uuid,
        area: area
    }

    // значения в чекбоксах
    const inputs = event.target.querySelectorAll('input[type="checkbox"]')

    // значения с БД
    const carAreasEvents = carAreas.value?.filter(carArea => carArea.area == area)

    // текущие значения чекбоксов
    const carAreasEventsChecked = []

    // получить текущие значения чекбоксов
    inputs.forEach(elem => {
        if(elem.checked) {
            carAreasEventsChecked.push(elem.value)
        }
    })

    // Если есть активные чекбоксы
    if (carAreasEventsChecked.length) {

        for (const carEvent of carAreasEventsChecked) {
            
            const currentParam = carAreasEvents.find(item => item.type == carEvent)
            
            // Если чекбокс еще не включен
            if (!currentParam) {
                payload.type = carEvent

                // добавить запись
                await carAreasStore.createCarArea(payload)

                if (carAreasCreateMsg.value.success == false) requestErrors.push(carAreasCreateMsg.value.text)
            }              
        }

        // Проверка данных с БД
        if (carAreasEvents.length) {
            for (const carArea of carAreasEvents) {
                
                const paramData = carAreasEventsChecked.find(item => carArea.type == item)

                // Если чекбокс отключаем
                if (!paramData) {
                    // удалить запись
                    await carAreasStore.deleteCarArea(carArea.id)

                    if (carAreasDeleteMsg.value.success == false) requestErrors.push(carAreasDeleteMsg.value.text)
                }
            }
        }

    } else {
        // Если нет активных чекбоксов

        // Если в БД есть записи
        if (carAreasEvents.length) {
            
            for (const carArea of carAreasEvents) {

                // удалить запись
                await carAreasStore.deleteCarArea(carArea.id)

                if (carAreasDeleteMsg.value.success == false) requestErrors.push(carAreasDeleteMsg.value.text)
            }
        }
    }

    if (!requestErrors.length) {

        emit('submit-success')

        // Обновить состояние событий геозоны
        await loadCarAreas()
    }

}

onBeforeMount(async() => {
    const isUseStore = true
    const params = { 'c.limit': 6 }

    const requestAreas = await areasStore.readAreas(params, isUseStore)
    areas.value = requestAreas

    await loadCarAreas()
})

onBeforeUnmount(() => {
    clearCarAreasMsgs()

    if(areasMsg.value?.text) areasStore.setAreasMsg({})
})

watch(inputSearch, async (input) => {

    input.length > 2 ? 
        areas.value = await areasStore.readAreas({ 'c.limit': 6, 'c.q': input }, false) :
        areas.value = areasStore.areas

    if(areas.value?.array?.length) areasStore.setAreasMsg({})
})
</script>

<template>
    <div class="uta-car-areas-control">

        <div class="row mb-4 justify-content-between">
            <div class="col-12">
                <div class="uta-input__wrapper uta-search">
                    <input 
                        type="text" 
                        :placeholder="getTranslate.SEARCH + '...'" 
                        v-model="inputSearch"
                        @keyup.escape="inputSearch = ''"
                        class="form-control uta-input__wrapper-input uta-search__input uta-search__input--light" 
                    >
                    <template v-if="inputSearch.length > 2">
                        <span class="material-icons uta-input__wrapper-icon" @click="inputSearch = ''">
                            cancel
                        </span>
                    </template>
                    <template v-else>
                        <span class="material-icons uta-input__wrapper-icon">
                            search
                        </span>
                    </template>
                </div>
            </div>
        </div>

        <div 
            v-if="'success' in areasMsg && !areasMsg.success"
            class="my-4 uta-alert uta-alert--danger" 
        >
            <span class="material-icons uta-alert__icon">check_circle_outline</span>
            <div class="uta-alert__text">
                {{ getTranslate[areasMsg.text] || areasMsg.text }}
            </div>
        </div>

        <div 
            v-if="'success' in carAreasCreateMsg"
            class="my-4 uta-alert" 
            :class="{
                'uta-alert--success' : carAreasCreateMsg.success, 
                'uta-alert--danger' : !carAreasCreateMsg.success
            }"
        >
            <span class="material-icons uta-alert__icon">check_circle_outline</span>
            <div class="uta-alert__text">
                {{ getTranslate[carAreasCreateMsg.text] || carAreasCreateMsg.text }}
            </div>
            <button type="button" class="uta-alert__close" @click="carAreasStore.setCarAreasCreateMsg({})">
                <span class="material-icons">close</span>
            </button>
        </div>

        <div 
            v-if="'success' in carAreasDeleteMsg"
            class="my-4 uta-alert" 
            :class="{
                'uta-alert--success' : carAreasDeleteMsg.success, 
                'uta-alert--danger' : !carAreasDeleteMsg.success
            }"
        >
            <span class="material-icons uta-alert__icon">check_circle_outline</span>
            <div class="uta-alert__text">
                {{ getTranslate[carAreasDeleteMsg.text] || carAreasDeleteMsg.text }}
            </div>
            <button type="button" class="uta-alert__close" @click="carAreasStore.setCarAreasDeleteMsg({})">
                <span class="material-icons">close</span>
            </button>
        </div>

        <template v-if="areas.array?.length">
            <div class="mb-4 uta-list uta-car-areas-control__list">
                <form
                    v-for="(area, index) in areas.array"
                    :key="index"
                    class="row uta-list__item uta-car-areas-control__item"
                    @submit.prevent="submitForm($event, area.uuid)" 
                >
                    <div class="col-10 uta-car-areas-control__name" @click="showAttrs[index] = !showAttrs[index]">
                        <span href="#" class="uta-list__item-link uta-car-areas-control__name">
                            {{ area.name }}
                        </span>
                    </div>

                    <div class="col-2 text-end uta-car-areas-control__ctrl" @click="showAttrs[index] = !showAttrs[index]">
                        <span class="material-icons">
                            expand_more
                        </span>
                    </div>

                    <div class="col-12 pt-3 uta-car-areas-control__attrs" v-show="showAttrs[index]">

                        <!-- Вход в зону -->
                        <div class="form-check">
                            <input 
                                type="checkbox" 
                                class="form-check-input __attrs-attr"
                                name="protected_area_in"
                                value="protected_area_in"
                                :checked="checkSelected('protected_area_in', area.uuid)"
                                :id="'car-areas-event-area-in-' + area.uuid"
                                @change="changeCarEvent($event, area.uuid)"
                            > 
                            <label 
                                class="form-check-label" 
                                :for="'car-areas-event-area-in-' + area.uuid"
                            >
                                {{ getTranslate.EVENT_PROTECTED_AREA_IN }}
                            </label>
                        </div>

                        <!-- Выход из зоны -->
                        <div class="form-check">
                            <input 
                                type="checkbox"
                                class="form-check-input uta-car-areas-control__attrs-attr" 
                                name="protected_area"
                                value="protected_area"
                                :checked="checkSelected('protected_area', area.uuid)"
                                :id="'car-areas-event-area-' + area.uuid"
                                @change="changeCarEvent($event, area.uuid)"
                            > 
                            <label 
                                class="form-check-label" 
                                :for="'car-areas-event-area-' + area.uuid"
                            >
                                {{ getTranslate.EVENT_PROTECTED_AREA }}
                            </label>
                        </div>
                        <button 
                            type="submit" 
                            class="uta-btn uta-btn--main mt-3"
                            disabled
                        >{{ getTranslate.BTN_SAVE }}</button>
                    </div>
                </form>
            </div>

            <div class="uta-cars__pagination" v-if="areas.c && (areas.c.isBefore || areas.c.isAfter)">
                <div class="row">
                    <div class="col-12 col-sm-6">
                        <div class="uta-pagination">
                            <button 
                                :disabled="areasProcess || !areas.c.isBefore"
                                class="uta-btn uta-btn--sm uta-pagination__prev" 
                                @click.prevent="changePage(areas.c.start, 'before')"
                            >
                                <span class="material-icons">
                                    navigate_before
                                </span>
                            </button>
                            <div class="uta-pagination__current">
                                {{ pageNumber }}
                            </div>
                            <button 
                                :disabled="areasProcess || !areas.c.isAfter"
                                class="uta-btn uta-btn--sm uta-pagination__next" 
                                @click.prevent="changePage(areas.c.stop, 'after')"
                            >
                                <span class="material-icons">
                                    navigate_next
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

        </template>
    </div>
</template>

<style lang="scss">
@import '@/assets/styles/scss/components/car/car-areas-control.scss';
</style>